import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Search, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";

import { useLang } from "@/context/LanguageContext";
import { classifyItems, reclassify } from "@/lib/api";
import { formatEUR } from "@/lib/i18n";
import { WIZARD } from "@/constants/testIds/wizard";

const SECTION_LABEL = {
  sp_attivo: { it: "Stato Patrimoniale — Attivo", en: "Balance Sheet — Assets" },
  sp_passivo: { it: "Stato Patrimoniale — Passivo", en: "Balance Sheet — Liabilities" },
  conto_economico: { it: "Conto Economico", en: "Income Statement" },
  other: { it: "Altro", en: "Other" },
};

export default function MappingStep({ items, priorYears = [], categories, sector = "generico", periodMonths = 12, onBack, onConfirmed }) {
  const { t, lang } = useLang();
  const [classified, setClassified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await classifyItems(items, lang);
        if (!cancelled) {
          setClassified(res);
          toast.success(t("toast_classify_ok"));
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("err_generic"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [items, lang, t]);

  const grouped = useMemo(() => {
    const groups = { sp_attivo: [], sp_passivo: [], conto_economico: [], other: [] };
    for (const c of categories) {
      const sec = c.section === "other" ? "other" : c.section;
      groups[sec].push(c);
    }
    return groups;
  }, [categories]);

  const updateItem = (idx, code) => {
    setClassified((prev) => prev.map((it, i) => (i === idx ? { ...it, code } : it)));
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return classified.map((it, idx) => ({ it, idx }));
    const q = query.toLowerCase();
    return classified
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => it.description.toLowerCase().includes(q) || it.code.toLowerCase().includes(q));
  }, [classified, query]);

  const confidenceColor = (v) => {
    if (v >= 0.8) return "bg-green-50 text-green-700 border-green-200";
    if (v >= 0.5) return "bg-amber-50 text-amber-800 border-amber-200";
    return "bg-red-50 text-red-700 border-red-200";
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      // Extract only items arrays for backend (label kept as-is)
      const py = priorYears.map((p) => ({ label: p.label, items: p.items }));
      const res = await reclassify(classified, lang, sector, null, py.length ? py : null, periodMonths);
      onConfirmed(classified, res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
            {t("mapping_title")}
          </h1>
          <p className="mt-2 max-w-2xl text-slate-600">{t("mapping_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
          <Sparkles className="h-3.5 w-3.5 text-blue-700" strokeWidth={2} />
          Claude Sonnet 4.5
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid={WIZARD.mappingSearch}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search_placeholder")}
            className="rounded-full pl-9"
          />
        </div>
        <div className="ml-auto text-xs text-slate-500">
          {classified.length} {lang === "it" ? "voci" : "items"}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-slate-600">
            <Loader2 className="h-6 w-6 animate-spin text-blue-700" />
            <div className="font-heading text-lg">{t("mapping_classifying")}</div>
          </div>
        ) : (
          <div className="max-h-[62vh] overflow-auto">
            <Table data-testid={WIZARD.mappingTable}>
              <TableHeader className="sticky top-0 bg-slate-50">
                <TableRow>
                  <TableHead className="w-[42%]">{t("mapping_col_desc")}</TableHead>
                  <TableHead className="text-right">{t("mapping_col_amount")}</TableHead>
                  <TableHead className="w-[34%]">{t("mapping_col_category")}</TableHead>
                  <TableHead className="w-[110px] text-center">{t("mapping_col_confidence")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(({ it, idx }) => (
                  <TableRow key={idx} data-testid={WIZARD.mappingRow}>
                    <TableCell className="py-2 text-slate-800">{it.description}</TableCell>
                    <TableCell className="py-2 text-right font-mono text-sm text-slate-900">
                      {formatEUR(it.amount, lang)}
                    </TableCell>
                    <TableCell className="py-2">
                      <Select value={it.code} onValueChange={(v) => updateItem(idx, v)}>
                        <SelectTrigger
                          data-testid={WIZARD.mappingSelect}
                          className="h-9 w-full min-w-[280px] bg-white text-left"
                        >
                          <SelectValue placeholder={t("mapping_select_placeholder")} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[360px] bg-white">
                          {["sp_attivo", "sp_passivo", "conto_economico", "other"].map((sec) => (
                            grouped[sec]?.length ? (
                              <SelectGroup key={sec}>
                                <SelectLabel className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                                  {SECTION_LABEL[sec][lang]}
                                </SelectLabel>
                                {grouped[sec].map((c) => (
                                  <SelectItem key={c.code} value={c.code}>
                                    <span className="mr-2 font-mono text-xs text-blue-700">{c.code}</span>
                                    {lang === "it" ? c.name_it : c.name_en}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ) : null
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <Badge variant="outline" className={`font-mono ${confidenceColor(it.confidence)}`}>
                        {Math.round((it.confidence || 0) * 100)}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button
          data-testid={WIZARD.backBtn}
          variant="outline"
          onClick={onBack}
          className="rounded-full border-slate-300"
        >
          <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={2} />
          {t("back")}
        </Button>
        <Button
          data-testid={WIZARD.confirmBtn}
          onClick={handleConfirm}
          disabled={loading || confirming || !classified.length}
          className="rounded-full bg-blue-700 px-6 py-5 text-white hover:bg-blue-800"
        >
          {confirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t("confirm_reclassify")}
          <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}
